import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts"
import { formatCurrency, formatGrowth } from "../helpers"

const ComparativoCard = ({ titulo, periodoActual, periodoComparado, valorActual, valorComparado, crecimientoPct }) => {
  const g = formatGrowth(crecimientoPct)
  return (
    <div
      style={{
        flex: "1 1 320px",
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 20,
        borderLeft: `4px solid ${g.color}`,
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 12 }}>
        {titulo}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{periodoComparado || "—"}</div>
          <div style={{ fontSize: 16, color: "#94a3b8", fontWeight: 500 }}>{formatCurrency(valorComparado)}</div>
        </div>
        <div style={{ fontSize: 22, color: "#cbd5e1" }}>→</div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>{periodoActual || "—"}</div>
          <div style={{ fontSize: 22, color: "#0f172a", fontWeight: 700 }}>{formatCurrency(valorActual)}</div>
        </div>
      </div>
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, color: "#64748b" }}>Crecimiento</span>
        <span style={{ fontSize: 18, fontWeight: 700, color: g.color }}>
          <i className={`mdi mdi-${g.icon} me-1`} />
          {g.text}
        </span>
      </div>
    </div>
  )
}

ComparativoCard.propTypes = {
  titulo: PropTypes.string,
  periodoActual: PropTypes.string,
  periodoComparado: PropTypes.string,
  valorActual: PropTypes.number,
  valorComparado: PropTypes.number,
  crecimientoPct: PropTypes.number,
}

const TooltipChart = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null
  return (
    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.fill }}>
          {p.name}: {formatCurrency(p.value)}
        </div>
      ))}
    </div>
  )
}

TooltipChart.propTypes = { active: PropTypes.bool, payload: PropTypes.array, label: PropTypes.string }

const ComparativoTab = ({ data }) => {
  const c = data?.comparativo
  const [vista, setVista] = useState("anterior") // "anterior" | "yoy"

  const sucursalesData = useMemo(() => {
    const arr = c?.sucursales || []
    return arr.map(s => ({
      sucursal: s.sucursal,
      sucursalCorta: (s.sucursal || "").length > 24 ? `${s.sucursal.slice(0, 23)}…` : s.sucursal,
      actual: Number(s.actual) || 0,
      comparado: Number(vista === "anterior" ? s.anterior : s.yoy) || 0,
      crecimiento: vista === "anterior" ? s.crecimiento_vs_anterior_pct : s.crecimiento_yoy_pct,
    }))
  }, [c, vista])

  const sucursalesOrdenadas = useMemo(() => {
    return [...sucursalesData].sort((a, b) => b.actual - a.actual).slice(0, 15)
  }, [sucursalesData])

  if (!c) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-chart-timeline-variant" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin información comparativa.</div>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Cards globales */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <ComparativoCard
          titulo="Período actual vs anterior"
          periodoActual={c.periodo_actual}
          periodoComparado={c.periodo_anterior}
          valorActual={c.global_actual}
          valorComparado={c.global_anterior}
          crecimientoPct={c.global_crecimiento_vs_anterior_pct}
        />
        <ComparativoCard
          titulo="Período actual vs año anterior (YoY)"
          periodoActual={c.periodo_actual}
          periodoComparado={c.periodo_yoy}
          valorActual={c.global_actual}
          valorComparado={c.global_yoy}
          crecimientoPct={c.global_crecimiento_yoy_pct}
        />
      </div>

      {/* Comparativo por sucursal */}
      {sucursalesData.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: 20 }}>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
              <i className="mdi mdi-chart-bar me-2 text-primary" />
              Comparativo por sucursal (top {sucursalesOrdenadas.length})
            </h6>
            <div style={{ display: "inline-flex", borderRadius: 6, background: "#f1f5f9", padding: 2 }}>
              {[
                { v: "anterior", l: "vs período anterior" },
                { v: "yoy", l: "vs año anterior" },
              ].map(o => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setVista(o.v)}
                  style={{
                    padding: "4px 12px",
                    border: "none",
                    background: vista === o.v ? "#fff" : "transparent",
                    color: vista === o.v ? "#0f172a" : "#64748b",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <div style={{ width: "100%", height: Math.max(320, sucursalesOrdenadas.length * 36) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sucursalesOrdenadas} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v)} />
                <YAxis type="category" dataKey="sucursalCorta" width={170} tick={{ fontSize: 11, fill: "#475569" }} interval={0} />
                <Tooltip content={<TooltipChart />} cursor={{ fill: "rgba(59,130,246,0.05)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="comparado" name={vista === "anterior" ? "Anterior" : "Año anterior"} fill="#94a3b8" radius={[0, 4, 4, 0]} />
                <Bar dataKey="actual" name="Actual" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tabla con crecimiento por sucursal */}
          <div style={{ marginTop: 20, maxHeight: 320, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
                <tr>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Sucursal</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>{vista === "anterior" ? "Anterior" : "Año anterior"}</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Actual</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Crecimiento</th>
                </tr>
              </thead>
              <tbody>
                {sucursalesData.map((s, i) => {
                  const g = formatGrowth(s.crecimiento)
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 12px", color: "#0f172a", fontWeight: 500 }}>{s.sucursal}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", color: "#94a3b8" }}>{formatCurrency(s.comparado)}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", color: "#0f172a", fontWeight: 600 }}>{formatCurrency(s.actual)}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", color: g.color, fontWeight: 700 }}>
                        <i className={`mdi mdi-${g.icon} me-1`} />
                        {g.text}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

ComparativoTab.propTypes = { data: PropTypes.object }

export default ComparativoTab
